import { useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    Loader2,
    ChevronDown,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
    History,
    XCircle,
    RefreshCw,
    CheckCircle2,
    UserPlus,
    UserMinus,
    CalendarPlus,
    ClipboardCheck,
    ClipboardX,
} from 'lucide-react';
import { useClientActivityQuery } from '../../services/hooks/useClientes';
import type { ClientActivity, ClientActivityType } from '../../services/api/clienteService';

interface ClientActivitySectionProps {
    clientId?: string;
}

const DIAS: Record<string, string> = {
    lunes: 'Lunes',
    martes: 'Martes',
    miercoles: 'Miércoles',
    'miércoles': 'Miércoles',
    jueves: 'Jueves',
    viernes: 'Viernes',
    sabado: 'Sábado',
    'sábado': 'Sábado',
    domingo: 'Domingo',
};

// Presentación por tipo de actividad: icono, color y etiqueta.
const PRESENTATION: Record<
    ClientActivityType,
    { label: string; icon: typeof History; className: string }
> = {
    reservation: { label: 'Reserva', icon: CalendarPlus, className: 'text-blue-600 bg-blue-50' },
    cancellation: { label: 'Cancelación', icon: XCircle, className: 'text-red-600 bg-red-50' },
    recovery_earned: { label: 'Recuperación ganada', icon: RefreshCw, className: 'text-amber-600 bg-amber-50' },
    recovery_used: { label: 'Recuperación usada', icon: CheckCircle2, className: 'text-green-600 bg-green-50' },
    enrollment_added: { label: 'Plaza asignada', icon: UserPlus, className: 'text-indigo-600 bg-indigo-50' },
    enrollment_removed: { label: 'Plaza retirada', icon: UserMinus, className: 'text-gray-600 bg-gray-100' },
    // E3 — una clase suelta, no una plaza. La etiqueta dice "el centro" porque
    // esto solo lo hace un administrador desde el detalle del evento: si se lee
    // como si lo hubiera hecho el cliente, no hay forma de entender el historial.
    class_added: { label: 'Añadido a una clase por el centro', icon: UserPlus, className: 'text-indigo-600 bg-indigo-50' },
    class_removed: { label: 'Sacado de una clase por el centro', icon: UserMinus, className: 'text-gray-600 bg-gray-100' },
    // Sin esta fila, retirar una recuperación dejaba el historial con el
    // "Recuperación ganada" y nada que explicara por qué ya no está.
    recovery_revoked: { label: 'Recuperación retirada por el centro', icon: XCircle, className: 'text-gray-600 bg-gray-100' },
    // Asistencia. «Vino» y «No vino» a propósito, que es como lo dice el
    // centro; no hay un tercer estado porque lo que nadie marca no genera fila.
    attendance_present: { label: 'Vino a clase', icon: ClipboardCheck, className: 'text-green-700 bg-green-50' },
    attendance_absent: { label: 'No vino a clase', icon: ClipboardX, className: 'text-red-600 bg-red-50' },
};

const levelName = (activity: ClientActivity): string | undefined => {
    if (activity.meta?.levelName) return activity.meta.levelName;
    if (activity.levelId && typeof activity.levelId === 'object') return activity.levelId.nombre;
    return undefined;
};

// Frase legible que describe el "qué/cuándo" de una fila.
const describe = (activity: ClientActivity): string => {
    const nivel = levelName(activity);
    const { meta } = activity;

    if (activity.type === 'enrollment_added' || activity.type === 'enrollment_removed') {
        const dia = meta?.dia ? DIAS[meta.dia.toLowerCase()] ?? meta.dia : undefined;
        const parts = [nivel, dia, meta?.horaInicio].filter(Boolean);
        return parts.length ? parts.join(' · ') : 'Plaza recurrente';
    }

    // Actividades ligadas a una clase concreta.
    const fechaTxt = meta?.fecha
        ? format(new Date(meta.fecha), "EEEE d MMM", { locale: es })
        : undefined;
    const parts = [nivel, fechaTxt, meta?.horaInicio].filter(Boolean);
    let base = parts.length ? parts.join(' · ') : 'Clase';

    if (activity.type === 'recovery_earned' && meta?.source === 'admin_cancel') {
        base += ' (cancelada por el centro)';
    } else if (activity.type === 'recovery_earned' && meta?.source === 'holiday') {
        // P3 — "cancelada por el centro" también sería verdad, pero el cliente no
        // necesita preguntar por qué: el día era festivo.
        base += ' (día festivo)';
    } else if (activity.type === 'recovery_earned' && meta?.source === 'enrollment_adjustment') {
        base += ' (ajuste de alta)';
    } else if (activity.type === 'recovery_earned' && meta?.source === 'manual') {
        // E2 — una recuperación concedida a mano no sale de ninguna clase, así
        // que `parts` viene vacío y la fila se quedaba en un "Clase" pelado,
        // indistinguible de una ganada por cancelar.
        base = 'Concedida a mano por el centro';
    }

    if (activity.type === 'recovery_revoked') {
        // La de cortesía no sale de ninguna clase, así que `parts` viene vacío
        // y la fila se quedaría en un "Clase" pelado.
        const origen = meta?.source === 'manual'
            ? 'Concedida a mano por el centro'
            : meta?.source === 'holiday' ? `${base} (día festivo)` : base;
        return meta?.reason ? `${origen} — ${meta.reason}` : origen;
    }

    // Una clase que canceló el CENTRO, no el cliente. Sin decirlo, la fila se
    // lee como si hubiera cancelado él, que es lo contrario de lo que pasó.
    if (activity.type === 'cancellation' && meta?.source === 'admin_cancel') {
        return `${base} (cancelada por el centro)`;
    }
    if (activity.type === 'cancellation' && meta?.source === 'holiday') {
        return `${base} (cancelada por ser día festivo)`;
    }

    // E1 — el motivo por el que una cancelación no dio recuperación. El backend
    // lo calcula desde siempre; hasta B9 el esquema lo tiraba al guardar, así
    // que las filas anteriores a ese despliegue no lo traen y se quedan como
    // estaban.
    if (activity.type === 'cancellation' && meta?.recoveryGranted === false) {
        base += meta.reason === 'outside_horizon'
            ? ' — sin recuperación (canceló con demasiada antelación)'
            : ` — sin recuperación (avisó con ${meta.hoursUntilClass ?? '?'} h)`;
    }

    // E3 — quién lo hizo. Sin esto la fila no dice si fue el centro o el propio
    // cliente, que es justo lo que hubo que reconstruir a mano.
    if ((activity.type === 'class_added' || activity.type === 'class_removed') && meta?.actor) {
        base += ` — ${meta.actor}`;
    }

    // P6 — quién pasó la lista. Una ausencia le cuesta la clase al cliente, así
    // que la fila tiene que decir quién la marcó.
    if (
        (activity.type === 'attendance_present' || activity.type === 'attendance_absent')
        && meta?.actor
    ) {
        base += ` — ${meta.actor}`;
    }

    return base;
};

const PAGE_SIZE = 20;

const ClientActivitySection = ({ clientId }: ClientActivitySectionProps) => {
    const [open, setOpen] = useState(false);
    const [page, setPage] = useState(1);
    const { data, isLoading, isFetching } = useClientActivityQuery(clientId, {
        enabled: open,
        page,
        limit: PAGE_SIZE,
    });

    const activity = data?.items ?? [];
    const pages = data?.pages ?? 1;
    const total = data?.total ?? 0;

    // Rango mostrado en esta página: "Mostrando 21–40 de 93".
    const rangeFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const rangeTo = total === 0 ? 0 : rangeFrom + activity.length - 1;

    return (
        <div className="bg-white rounded-lg shadow p-6 mt-6">
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full flex justify-between items-center"
            >
                <div className="flex items-center">
                    <History className="mr-2" size={20} />
                    <h2 className="text-lg font-bold">Historial de actividad</h2>
                </div>
                {open ? <ChevronUp /> : <ChevronDown />}
            </button>

            {open && (
                <>
                    <p className="text-xs text-gray-400 mt-1">
                        Reservas, cancelaciones, recuperaciones y cambios de plaza o de clase de los últimos ~2 meses.
                    </p>

                    {isLoading ? (
                        <div className="flex justify-center my-4">
                            <Loader2 className="animate-spin" />
                        </div>
                    ) : activity && activity.length > 0 ? (
                        <ul className="mt-4 space-y-2">
                            {activity.map((item) => {
                                const pres = PRESENTATION[item.type];
                                const Icon = pres?.icon ?? History;
                                return (
                                    <li
                                        key={item._id}
                                        className="flex items-start gap-3 border rounded-lg p-3"
                                    >
                                        <span className={`mt-0.5 rounded-full p-1.5 ${pres?.className ?? 'text-gray-600 bg-gray-100'}`}>
                                            <Icon size={16} />
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-sm">{pres?.label ?? item.type}</div>
                                            <div className="text-sm text-gray-600">{describe(item)}</div>
                                        </div>
                                        <div className="text-xs text-gray-400 whitespace-nowrap">
                                            {format(new Date(item.occurredAt), "d MMM, HH:mm", { locale: es })}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p className="text-gray-500 my-4">No hay actividad registrada para este cliente.</p>
                    )}

                    {/* Paginación */}
                    {total > 0 && (
                        <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
                            <span>
                                Mostrando {rangeFrom}–{rangeTo} de {total} registro{total !== 1 ? 's' : ''}
                                {isFetching && <Loader2 className="animate-spin inline ml-2" size={14} />}
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1}
                                    className="p-1 rounded border disabled:opacity-40 hover:bg-gray-50"
                                    aria-label="Página anterior"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <span>Página {page} de {pages}</span>
                                <button
                                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                                    disabled={page >= pages}
                                    className="p-1 rounded border disabled:opacity-40 hover:bg-gray-50"
                                    aria-label="Página siguiente"
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ClientActivitySection;
